import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  approvalDecisionInputSchema,
  appCategoriesSchema,
  approvalCardSchema,
  approvalListSchema,
  attemptSchema,
  availableInChatInputSchema,
  connectionCatalogSchema,
  connectionDetailSchema,
  connectionListSchema,
  eventListSchema,
  labelInputSchema,
  membersPersonalQuerySchema,
  newWebsiteLoginInputSchema,
  permissionsSchema,
  replacePasswordInputSchema,
  standingApprovalListSchema,
  standingApprovalQuerySchema,
  startAttemptInputSchema,
  teammatesInputSchema,
  updatePermissionsInputSchema,
  websiteLoginListSchema,
  websiteLoginSchema,
} from "./connections";
import { catalogErrors } from "./errors";
import { idSchema } from "./ids";
import { forStepSchema } from "./processes";

/**
 * The regional Connections API as one contract: the server implements it and the browser calls it,
 * and neither imports the other. Paths, methods and statuses are the HTTP surface; the server
 * mounts this under `/api`.
 *
 * Inputs are "detailed" so path parameters stay apart from the body. A malformed ID names
 * something that cannot exist, so the server answers an issue under `params` with NOT_FOUND and
 * anything else with VALIDATION_ERROR. Every procedure can refuse with the catalog codes below,
 * in the shared error envelope.
 */
const workspace = z.object({ workspaceId: z.string().min(1) });
const onConnection = z.object({
  params: workspace.extend({ connectionId: idSchema("connection") }),
});
const onAttempt = z.object({ params: workspace.extend({ attemptId: idSchema("attempt") }) });
const onApproval = workspace.extend({ approvalId: idSchema("approval") });
const onStandingApproval = z.object({
  params: workspace.extend({ standingApprovalId: idSchema("standingApproval") }),
});
const inWorkspace = z.object({ params: workspace });
// The reads an owner or admin may widen to members' personal connections, read-only. The query may
// be left out entirely, as every caller did before the opt-in existed.
const inspection = membersPersonalQuerySchema.default({});
const inspectable = inWorkspace.extend({ query: inspection });
const onApp = z.object({ params: workspace.extend({ app: z.string().min(1).max(100) }) });

const route = oc.$route({ inputStructure: "detailed" });

export const connectionsContract = oc
  .errors(
    catalogErrors([
      "UNAUTHORIZED",
      "FORBIDDEN",
      "NOT_FOUND",
      "CONFLICT",
      "CONNECTION_NEEDS_RENEWAL",
      "VALIDATION_ERROR",
      "INTERNAL_ERROR",
      "PROVIDER_UNAVAILABLE",
      "AUTH_UNAVAILABLE",
      "SERVICE_UNAVAILABLE",
    ]),
  )
  .tag("Connections")
  .prefix("/workspaces/{workspaceId}/connections")
  .router({
    // Static segments are declared first; the router prefers them over a connection ID either way.
    catalog: route
      .route({ method: "GET", path: "/apps" })
      .input(inWorkspace)
      .output(connectionCatalogSchema),
    appCategories: route
      .route({ method: "GET", path: "/apps/{app}/categories" })
      .input(onApp)
      .output(appCategoriesSchema),
    startAttempt: route
      .route({ method: "POST", path: "/attempts", successStatus: 201 })
      // `forStep`: the draft, step and use a sign-in starts from, whose account it sets when it
      // connects (plan Task 16). Here rather than in `startAttemptInputSchema`, since the processes
      // contracts already import the connections ones.
      .input(
        inWorkspace.extend({
          body: startAttemptInputSchema.extend({ forStep: forStepSchema.optional() }),
        }),
      )
      .output(attemptSchema),
    attempt: route
      .route({ method: "GET", path: "/attempts/{attemptId}" })
      .input(onAttempt)
      .output(attemptSchema),
    completeAttempt: route
      .route({ method: "POST", path: "/attempts/{attemptId}/complete" })
      .input(onAttempt)
      .output(attemptSchema),
    approvals: route
      .route({ method: "GET", path: "/approvals" })
      .input(inWorkspace)
      .output(approvalListSchema),
    // What a chat's approval will do, with its arguments: for the one person who may decide it.
    // Anyone else is told NOT_FOUND, as for an approval that does not exist.
    approvalCard: route
      .route({ method: "GET", path: "/approvals/{approvalId}/card" })
      .input(z.object({ params: onApproval }))
      .output(approvalCardSchema),
    decideApproval: route
      .route({ method: "POST", path: "/approvals/{approvalId}/decision" })
      .input(z.object({ params: onApproval, body: approvalDecisionInputSchema }))
      .output(approvalListSchema),
    // "For this chat" choices in force: each visible to whoever chose it and to the connection's
    // managers, who may revoke it.
    standingApprovals: route
      .route({ method: "GET", path: "/standing-approvals" })
      .input(inWorkspace.extend({ query: standingApprovalQuerySchema }))
      .output(standingApprovalListSchema),
    revokeStandingApproval: route
      .route({
        method: "DELETE",
        path: "/standing-approvals/{standingApprovalId}",
        successStatus: 204,
      })
      .input(onStandingApproval),
    // Website logins: a site, a username and a password saved encrypted. No answer carries the
    // password; replacing it is the only way to change it.
    websiteLogins: route
      .route({ method: "GET", path: "/website-logins" })
      .input(inspectable)
      .output(websiteLoginListSchema),
    createWebsiteLogin: route
      .route({ method: "POST", path: "/website-logins", successStatus: 201 })
      .input(inWorkspace.extend({ body: newWebsiteLoginInputSchema }))
      .output(websiteLoginSchema),
    replaceWebsitePassword: route
      .route({ method: "PUT", path: "/website-logins/{connectionId}/password" })
      .input(onConnection.extend({ body: replacePasswordInputSchema }))
      .output(websiteLoginSchema),
    removeWebsiteLogin: route
      .route({ method: "DELETE", path: "/website-logins/{connectionId}", successStatus: 204 })
      .input(onConnection),
    list: route.route({ method: "GET", path: "/" }).input(inspectable).output(connectionListSchema),
    detail: route
      .route({ method: "GET", path: "/{connectionId}" })
      .input(onConnection.extend({ query: inspection }))
      .output(connectionDetailSchema),
    setLabel: route
      .route({ method: "PATCH", path: "/{connectionId}" })
      .input(onConnection.extend({ body: labelInputSchema }))
      .output(connectionDetailSchema),
    setTeammates: route
      .route({ method: "PUT", path: "/{connectionId}/teammates" })
      .input(onConnection.extend({ body: teammatesInputSchema }))
      .output(connectionDetailSchema),
    setAvailableInChat: route
      .route({ method: "PUT", path: "/{connectionId}/available-in-chat" })
      .input(onConnection.extend({ body: availableInChatInputSchema }))
      .output(connectionDetailSchema),
    permissions: route
      .route({ method: "GET", path: "/{connectionId}/permissions" })
      .input(onConnection)
      .output(permissionsSchema),
    setPermissions: route
      .route({ method: "PUT", path: "/{connectionId}/permissions" })
      .input(onConnection.extend({ body: updatePermissionsInputSchema }))
      .output(permissionsSchema),
    pause: route
      .route({ method: "POST", path: "/{connectionId}/pause" })
      .input(onConnection)
      .output(connectionDetailSchema),
    resume: route
      .route({ method: "POST", path: "/{connectionId}/resume" })
      .input(onConnection)
      .output(connectionDetailSchema),
    disconnect: route
      .route({ method: "DELETE", path: "/{connectionId}" })
      .input(onConnection)
      .output(connectionDetailSchema),
    events: route
      .route({ method: "GET", path: "/{connectionId}/events" })
      .input(onConnection)
      .output(eventListSchema),
  });

export type ConnectionsContract = typeof connectionsContract;
